import React from 'react';
import { Box, Typography, Modal, IconButton, Stack, Chip, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { tokens, withAlpha } from '../../Common/designTokens';

const CHARACTERISTICS_ORDER = [
  "Rungs",           // numberOfRungs
  "Amount Per Rung", // amountPerRung
  "Total Amount",    // totalAmount
  "Total Maturity",  // totalMaturity
];

const ViewAllFiltersModal = ({ 
  open, 
  onClose, 
  filters, 
  formatFieldLabel,
  input, 
  onDeleteChip, 
  onClearFilters, 
  formatRange, 
  formatBooleanValue,
  readOnly = false, // Control deletion functionality
  onEditFilter = undefined, // New prop for edit functionality
  onModifyCharacteristic = undefined, // New prop
  loadingCharacteristic = false, // New prop
  ladderName = null,
  chipSxResolver = null
}) => {
  // Local fallback label formatter (used if formatFieldLabel prop not provided)
  const localFormatFieldLabel = React.useCallback((key) => {
    if (!key || typeof key !== "string") return key;

    // Known mapping for characteristics (keep in sync with parent if needed)
    const labelMap = {
      numberOfRungs: "Rungs",
      amountPerRung: "Amount Per Rung",
      totalAmount: "Total Amount",
      totalMaturity: "Total Maturity",
    };
    if (labelMap[key]) return labelMap[key];
    if (formatFieldLabel) return formatFieldLabel(key);

    // Generic conversion: camelCase, snake_case, kebab-case to Title Case
    const spaced = key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_\-\.\:]+/g, " ")
      .trim();

    const words = spaced.split(/\s+/).map((w) => {
      if (w.toUpperCase() === w) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    });

    return words.join(" ");
  }, [formatFieldLabel]);

  const localFormatFilterValue = React.useCallback((key, value) => {
    if (formatBooleanValue) return formatBooleanValue(key, value);

    const normalizedKey = typeof key === "string" ? key.split("__")[0] : key;
    const booleanFields = [
      "zeroCoupon",
      "insured",
      "altMinTax",
      "taxable",
      "callable",
      "call",
      "perpetual",
    ];

    if (booleanFields.includes(normalizedKey)) {
      const normalizedValue = `${value}`.trim().toLowerCase();
      return normalizedValue === "1" || normalizedValue === "y" || normalizedValue === "yes"
        ? "Yes"
        : "No";
    }

    return value;
  }, [formatBooleanValue]);

  const handleChipDelete = (key) => {
    // If this is the last filter, perform a complete clear
    if (filters.length === 1) {
      onClearFilters();
    } else {
      // Otherwise delete just this filter
      onDeleteChip(key);
    }
  };
  // Sort filters according to CHARACTERISTICS_ORDER, then the rest
  const sortedFilters = React.useMemo(() => {
    if (!filters) return [];
    const orderMap = {
      "numberOfRungs": "Rungs",
      "amountPerRung": "Amount Per Rung",
      "totalAmount": "Total Amount",
      "totalMaturity": "Total Maturity",
    };
    // Map keys to display labels for sorting
    const filtersWithLabels = filters.map(([key, value]) => {
      let displayLabel = orderMap[key] || localFormatFieldLabel(key);
      return { key, value, displayLabel };
    });

    // Sort by CHARACTERISTICS_ORDER first, then the rest
    const ordered = [];
    CHARACTERISTICS_ORDER.forEach(orderLabel => {
      const found = filtersWithLabels.find(f => f.displayLabel === orderLabel);
      if (found) ordered.push([found.key, found.value]);
    });
    filtersWithLabels.forEach(f => {
      if (!CHARACTERISTICS_ORDER.includes(f.displayLabel)) {
        ordered.push([f.key, f.value]);
      }
    });
    return ordered;
  }, [filters, localFormatFieldLabel]);

  const handleModalClose = (event) => {
    event?.stopPropagation?.();
    onClose?.();
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="view-all-filters-modal-title"
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: tokens.colors.bodyBackgroundColor,
        boxShadow: 24,
        p: 0,
        outline: 'none',
        border: `1px solid ${tokens.colors.bodyBorderColor50}`,
        borderRadius: "8px",
      }}>
        {/* Modal Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          pb: 1.5,
          borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
        }}>
          <Typography 
            id="view-all-filters-modal-title" 
            variant="h6" 
            fontWeight="bold"
            sx={{ color: tokens.colors.fontColour }}
          >
            {ladderName ? `${ladderName}` : "Applied Filters"}
          </Typography>
          <IconButton 
            onClick={(event) => {
              event.stopPropagation();
              onClose?.();
            }}
            sx={{ color: tokens.colors.fontColour }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Modal Content */}
        <Box sx={{ 
          p: 3, 
          maxHeight: "calc(80vh - 170px)",
          overflowY: "auto",
        }}>
          <Stack 
            direction="row" 
            spacing={1.5} 
            useFlexGap 
            flexWrap="wrap" 
            sx={{ width: "100%" }}
          >
            {sortedFilters.map(([key, value]) => {
              let label = "";
               // First check if this is explicitly NOT a range field
              
              if ((key.startsWith("min") || key.startsWith("max"))
              && (key !== "minimumRating" && key !== "underlyingRating" && key !== "minimumRatingMoody")
              ) {
                const baseKey = key.replace(/^(min|max)/, "");
                const minValue = input[`min${baseKey}`];
                const maxValue = input[`max${baseKey}`];

                if (key.startsWith("min") || (key.startsWith("max") && minValue === undefined)) {
                  // Use formatted baseKey label
                  label = `${localFormatFieldLabel(baseKey)}: ${formatRange(minValue, maxValue, baseKey)}`;
                } else {
                  return null;
                }
              } else {
                label = `${localFormatFieldLabel(key)}: ${localFormatFilterValue(key, value)}`;
              }

              // --- NEW: resolve chipSxResolver result and support { valueColor } ---
              const resolverResult = chipSxResolver ? chipSxResolver(key, value) : undefined;
              let valueColor;
              let additionalChipSx = {};
              if (resolverResult && typeof resolverResult === "object") {
                if ("valueColor" in resolverResult) {
                  valueColor = resolverResult.valueColor;
                  additionalChipSx = { ...resolverResult };
                  delete additionalChipSx.valueColor;
                } else {
                  additionalChipSx = resolverResult;
                }
              }

              // Factored Securities can be multi-select; in the modal there's no
              // space constraint, so let it wrap and show the full value.
              const normalizedKey = typeof key === "string" ? key.split("__")[0] : key;
              const isFactoredSecurities = normalizedKey === "securityCharge";

              const chipRootSx = {
                borderRadius: "8px",
                fontFamily: "Lato",
                fontWeight: 700,
                fontSize: "14px",
                lineHeight: "16px",
                textTransform: "none",
                backgroundColor: tokens.colors.defaultGridRowColor,
                color: tokens.colors.fontColour,
                border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                "& .MuiChip-deleteIcon": { color: tokens.colors.fontColour, fontSize: "14px" },
                ...(isFactoredSecurities && {
                  height: "auto",
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    overflow: "visible",
                    textOverflow: "clip",
                    display: "block",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                  },
                }),
                ...(additionalChipSx && Object.keys(additionalChipSx).length ? additionalChipSx : {}),
              };

              // If valueColor provided, color only the value portion after ": "
              let renderedLabel = label;
              if (valueColor && typeof label === "string") {
                const sep = ": ";
                const idx = label.indexOf(sep);
                if (idx !== -1) {
                  const prefix = label.slice(0, idx + sep.length);
                  const val = label.slice(idx + sep.length);
                  renderedLabel = (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span>{prefix}</span>
                      <span style={{ color: valueColor }}>{val}</span>
                    </span>
                  );
                } else {
                  renderedLabel = <span style={{ color: valueColor }}>{label}</span>;
                }
              }

              return label ? (
                <Chip
                  sx={chipRootSx}
                  deleteIcon={!readOnly ? <CloseIcon style={{ fontSize: "14px", color: tokens.colors.fontColour }} /> : null}
                  onDelete={!readOnly ? () => handleChipDelete(key) : undefined}
                  label={renderedLabel}
                  variant="outlined"
                />
              ) : null;
            })}
          </Stack>
        </Box>

        {/* Modal Footer with action buttons */}
        {!readOnly && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            p: 2,
            borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }}>
            {onModifyCharacteristic ? (
              <Button 
                variant="outlined" 
                onClick={(event) => {
                  event.stopPropagation();
                  onModifyCharacteristic();
                }}
                disabled={loadingCharacteristic}
                startIcon={loadingCharacteristic ? <CircularProgress size={14} color="inherit" /> : <EditIcon />}
                sx={{ textTransform: 'none', borderRadius: "8px", color: tokens.colors.fontColour, borderColor: tokens.colors.bodyBorderColor50 }}
              >
                {loadingCharacteristic ? "Loading..." : "Edit Characteristic"}
              </Button>
             ) : (
                onEditFilter && (
                  <Button 
                    variant="outlined" 
                    onClick={(event) => {
                      event.stopPropagation();
                      onEditFilter();
                    }}
                    startIcon={<EditIcon />}
                    sx={{ textTransform: 'none', borderRadius: "8px", }}
                  >
                    Edit Filters
                  </Button>
                )
              )}
            <Button 
              variant="contained" 
              onClick={(event) => {
                event.stopPropagation();
                onClearFilters();
              }}
              sx={{ textTransform: 'none', borderRadius: "8px", backgroundColor: tokens.palette.primary, color: tokens.colors.white, '&:hover': { backgroundColor: tokens.palette.primaryA80 } }}
            >
              Clear All
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default ViewAllFiltersModal;
